import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, Users, X } from "lucide-react";
import { useUserContext } from "../context/UserContext";
import { useCommunityWinsUnseen } from "../hooks/useCommunityWinsUnseen";
import { useTranslation } from "../context/LanguageContext";
import { isUserSetUp, isFreeTier } from "../utils/userUtils";
import SidebarRetailSelector from "./SidebarRetailSelector";
import DiscordLink from "./DiscordLink";
import AnnouncementButton from "./AnnouncementButton";
import GlobalFavoritesButton from "./GlobalFavoritesButton";
import LandingProductsMenu from "./LandingProductsMenu";
import { isHomeFeedPath, isPublicDealsPath } from "../config/publicDeals";
import { buildGetStartedUrl, sanitizePublicDealsReturnTo } from "../utils/authReturn";
import { track } from "../utils/analytics";
import { isBestDealsPath } from "../utils/bestDealsNavigation";

interface NavBarProps {
  onMenuClick: () => void;
  hideMenuOnMobile?: boolean;
}

const PUBLIC_AUTH_ROUTES = new Set([
  "/get-started",
  "/forgot-password",
  "/reset-password",
  // Legacy auth paths still mount briefly during the redirect to /get-started.
  "/login",
  "/register",
  "/signup",
]);

export default function NavBar({ onMenuClick, hideMenuOnMobile = false }: NavBarProps) {
  const { user } = useUserContext();
  const { hasNew: hasNewCommunityWins, unseenCount: unseenCommunityWins } = useCommunityWinsUnseen();
  const { t } = useTranslation();
  const location = useLocation();
  const isLandingPage = location.pathname === "/" || (!user && isHomeFeedPath(location.pathname));
  const isFeaturePage = location.pathname.startsWith("/features/");
  const isAuthPage = PUBLIC_AUTH_ROUTES.has(location.pathname);
  const isGuestDealsPage = !user && isPublicDealsPath(location.pathname);
  const isBestDealsPage = isBestDealsPath(location.pathname);
  const isSignedInDealsPage = Boolean(user) && /^\/[^/]+\/products\/?$/.test(location.pathname);
  const guestDealsReturnTo = sanitizePublicDealsReturnTo(
    `${location.pathname}${location.search}`
  ) || "/deals";
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Marketing surface = landing page, feature pages, or public auth page when not signed in.
  // We render a stripped-down navbar with logo + Products dropdown + Get Started button.
  const isMarketingSurface =
    (isLandingPage || isAuthPage || isFeaturePage) && !user;

  const landingHeaderStyle = "bg-transparent border-b border-transparent";
  const defaultHeaderStyle =
    "bg-primary-900 dark:bg-dark-900 border-b border-primary-800/50 dark:border-dark-700/50";
  const headerClass = isMarketingSurface ? landingHeaderStyle : defaultHeaderStyle;
  const positionClass = isMarketingSurface ? "absolute inset-x-0 top-0" : "sticky top-0";

  // Auth pages render their own back + logo header inside PintoAuthShell.
  if (isAuthPage && !user) return null;

  // The landing page renders its own light-theme sticky header.
  if (isLandingPage && !user) return null;

  return (
    <header className={`${positionClass} z-50 ${headerClass} ${user ? "md:hidden" : ""}`}>
      <div className={`${isGuestDealsPage || isBestDealsPage ? "max-w-[1600px] px-3" : "max-w-7xl px-4"} mx-auto sm:px-6 lg:px-8`}>
        <div className={`flex items-center justify-between ${isGuestDealsPage || isBestDealsPage || isSignedInDealsPage ? "h-12" : "h-14"}`}>
          {/* Left Section */}
          <div className={`flex min-w-0 items-center ${isBestDealsPage ? "gap-2" : "gap-3"}`}>
            {user && !hideMenuOnMobile && (
              <button
                onPointerDown={onMenuClick}
                className="md:hidden p-2 -ml-2 text-dark-200 hover:text-white hover:bg-white/10 rounded-xl transition-all"
                aria-label="Open menu"
              >
                <Menu size={22} />
              </button>
            )}
            <Link
              to="/"
              className="flex items-center gap-2 flex-shrink-0 hover:opacity-80 transition-opacity"
            >
              {/* Marketing surfaces (landing + /get-started) sit on a colored
                  gradient → always white logo lockup. Authed surfaces use a
                  dark navbar → white lockup as well. */}
              <img
                src="/hawk_icon_white.svg"
                alt="DealHawk"
                className={isGuestDealsPage || isBestDealsPage ? "h-7 w-7" : "h-8 w-8"}
              />
            </Link>
            {(isUserSetUp(user) || isFreeTier(user)) && (
              <SidebarRetailSelector compact variant="light" />
            )}
            {isMarketingSurface && !isAuthPage && (
              <nav className="hidden md:flex items-center gap-1 ml-2">
                <LandingProductsMenu variant="light" />
                <a
                  href="/#pricing"
                  className="text-sm font-semibold text-white/95 hover:text-white px-3 py-2 rounded-full hover:bg-white/10 transition-colors"
                >
                  Pricing
                </a>
              </nav>
            )}
          </div>

          {/* Right Section */}
          {/* Same buttons, same order, on every signed-in page (Best Deals keeps its own bar). */}
          {user && (isSignedInDealsPage || !isBestDealsPage) ? (
            <div className="flex items-center gap-1">
              {!isFreeTier(user) && <GlobalFavoritesButton iconSize={22} variant="light" />}
              {user.retailers && user.retailers.length > 0 && (
                <AnnouncementButton iconSize={22} variant="light" />
              )}
              <DiscordLink iconSize={22} variant="light" />
              <Link
                to="/community-successes"
                className={`rounded-xl p-2 transition-all hover:bg-white/10 ${location.pathname === "/community-successes" ? "text-white" : "text-white/80 hover:text-white"}`}
                aria-label={t("community.navLabel")}
                aria-current={location.pathname === "/community-successes" ? "page" : undefined}
              >
                <span className="relative inline-flex">
                  <Users size={22} />
                  {hasNewCommunityWins && location.pathname !== "/community-successes" && (
                    <span className="absolute -right-2 -top-1.5 flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-emerald-500 px-1 text-[10px] font-extrabold leading-none text-emerald-950 ring-2 ring-primary-900" aria-label={t("community.navNewWins", { count: unseenCommunityWins })}>
                      {unseenCommunityWins > 9 ? "9+" : unseenCommunityWins}
                    </span>
                  )}
                </span>
              </Link>
            </div>
          ) : !user ? (
            <div className="flex items-center gap-2">
              {isGuestDealsPage && (
                <Link
                  to={buildGetStartedUrl(guestDealsReturnTo, "guest_deals_nav_sign_in")}
                  onClick={() => track("guest_signin_started", { surface: "web", retailer: location.pathname.split("/")[2] || "walmart", trigger: "navigation", source: "guest_deals_nav_sign_in" })}
                  className="px-2 py-2 text-xs font-bold text-white/85 transition hover:text-white sm:px-3 sm:text-sm"
                >
                  Sign in
                </Link>
              )}
              {isMarketingSurface && !isAuthPage && (
                <button
                  type="button"
                  onClick={() => setMobileNavOpen((v) => !v)}
                  className="md:hidden p-2 text-white/95 hover:text-white hover:bg-white/10 rounded-xl transition-all"
                  aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
                  aria-expanded={mobileNavOpen}
                >
                  {mobileNavOpen ? <X size={22} /> : <Menu size={22} />}
                </button>
              )}
              {isGuestDealsPage ? (
                <Link
                  to={buildGetStartedUrl(guestDealsReturnTo, "guest_deals_nav_signup")}
                  onClick={() => track("guest_signup_started", { surface: "web", retailer: location.pathname.split("/")[2] || "walmart", trigger: "navigation", source: "guest_deals_nav_signup" })}
				  className="rounded-xl bg-white px-3 py-2 text-xs font-extrabold text-purple-900 transition hover:bg-purple-50 sm:px-4 sm:text-sm"
                >
				  <span className="sm:hidden">Create account</span>
				  <span className="hidden sm:inline">Create free account</span>
                </Link>
              ) : isLandingPage ? (
                <Link
                  to="/get-started"
                  className="bg-gradient-to-r from-[#5907c2] to-[#9d24eb] hover:from-[#6d28d9] hover:to-[#d131f3] text-white px-5 py-2.5 rounded-xl text-sm font-bold transition-all"
                >
                  {t("common.getStarted")}
                </Link>
              ) : (
                <Link
                  to="/get-started"
                  className="bg-white hover:bg-gray-100 text-violet-900 px-4 sm:px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors"
                >
                  {t("common.getStarted")}
                </Link>
              )}
            </div>
          ) : null}
        </div>

        {/* Mobile expanded nav for marketing surfaces */}
        {isMarketingSurface && !isAuthPage && mobileNavOpen && (
          <div className="md:hidden pb-4">
            <div className="bg-white/95 backdrop-blur-md rounded-2xl p-2 border border-white/20 shadow-xl">
              <LandingProductsMenu
                variant="dark"
                inline
                onNavigate={() => setMobileNavOpen(false)}
              />
              <a
                href="/#pricing"
                onClick={() => setMobileNavOpen(false)}
                className="block w-full px-4 py-3 rounded-xl font-semibold text-sm text-[#1e1b3a] hover:bg-violet-50"
              >
                Pricing
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
